// web/src/components/PreferencesCard.tsx — report emails, update mode, GBP managed (portal spec 14 / G2, A4).
// Used on the go-live checklist and in Settings. Saves all three at once (PATCH /v1/sites/:id/preferences, spec 11),
// so a saved card is a done preferences tick.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from './Toast';

export function PreferencesCard({ siteId, done, onSaved }: { siteId: string; done?: boolean; onSaved?: () => void }) {
  const qc = useQueryClient();
  const site = useQuery({ queryKey: ['site', siteId], queryFn: () => api.getSite(siteId) });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const [emails, setEmails] = useState('');
  const [mode, setMode] = useState<'auto' | 'show_me'>('show_me');
  const [gbp, setGbp] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const p = site.data?.preferences;
    if (!p) return;
    setEmails((p.reportEmails.length ? p.reportEmails : me.data ? [me.data.email] : []).join(', '));
    setMode(p.updateMode);
    setGbp(p.gbpManaged);
  }, [site.data, me.data]);

  const save = async () => {
    const list = emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (!list.length || gbp === null) { toast({ kind: 'error', message: 'Add an email and answer the Google question.' }); return; }
    setSaving(true);
    try {
      await api.setPreferences(siteId, { reportEmails: list, updateMode: mode, gbpManaged: gbp });
      toast({ kind: 'success', message: 'Saved.' });
      qc.invalidateQueries({ queryKey: ['site', siteId] });
      onSaved?.();
    } catch { toast({ kind: 'error', message: 'Couldn’t save. Check the email addresses and try again.' }); }
    finally { setSaving(false); }
  };

  const choice = (on: boolean) => `inline-flex min-h-11 items-center rounded-md border px-4 text-sm ${on ? 'border-run bg-run text-runink' : 'border-hair text-ink'}`;
  return (
    <div className="rounded-lg border border-hair bg-surface p-4 text-sm">
      <p className="font-head text-ink">Your preferences{done ? ' ✓' : ''}</p>
      <label className="mt-3 block text-muted">Where should we send your monthly report?
        <input value={emails} onChange={(e) => setEmails(e.target.value)} inputMode="email" autoCapitalize="none" autoComplete="email"
          className="mt-1 block min-h-11 w-full min-w-0 rounded-md border border-hair bg-bg px-3 text-ink" placeholder="you@business.com" />
      </label>
      <p className="mt-4 text-muted">When we improve your site each month:</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" aria-pressed={mode === 'auto'} onClick={() => setMode('auto')} className={choice(mode === 'auto')}>Update my site automatically</button>
        <button type="button" aria-pressed={mode === 'show_me'} onClick={() => setMode('show_me')} className={choice(mode === 'show_me')}>Show me first</button>
      </div>
      <p className="mt-4 text-muted">Manage your Google Business Profile as part of your plan?</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" aria-pressed={gbp === true} onClick={() => setGbp(true)} className={choice(gbp === true)}>Yes</button>
        <button type="button" aria-pressed={gbp === false} onClick={() => setGbp(false)} className={choice(gbp === false)}>No thanks</button>
      </div>
      <button onClick={save} disabled={saving} className="mt-4 inline-flex min-h-11 items-center rounded-md bg-run px-4 font-medium text-runink disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
    </div>
  );
}
